export class InputManager {
  private keys: Set<string> = new Set();
  private mouseMovement: { x: number; y: number } = { x: 0, y: 0 };
  private mouseButtons: Set<number> = new Set();
  private isPointerLocked: boolean = false;
  
  // Callbacks
  public onMaskSwitch: ((maskIndex: number) => void) | null = null;
  public onInteract: (() => void) | null = null;
  public onAbilityUse: (() => void) | null = null;
  
  constructor() {
    this.setupEventListeners();
  }
  
  private setupEventListeners(): void {
    // Keyboard events
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));
    
    // Mouse events
    window.addEventListener('mousemove', (e) => this.onMouseMove(e));
    window.addEventListener('mousedown', (e) => this.onMouseDown(e));
    window.addEventListener('mouseup', (e) => this.onMouseUp(e));
    
    // Pointer lock
    document.addEventListener('pointerlockchange', () => {
      this.isPointerLocked = document.pointerLockElement !== null;
    });
  }
  
  private onKeyDown(e: KeyboardEvent): void {
    this.keys.add(e.code);
    
    // Mask switching (1-5)
    if (e.code >= 'Digit1' && e.code <= 'Digit5') {
      const maskIndex = parseInt(e.code.replace('Digit', '')) - 1;
      this.onMaskSwitch?.(maskIndex);
    }
    
    // Interact
    if (e.code === 'KeyE') {
      this.onInteract?.();
    }
    
    // Use ability (left click or space)
    if (e.code === 'Space') {
      this.onAbilityUse?.();
    }
  }
  
  private onKeyUp(e: KeyboardEvent): void {
    this.keys.delete(e.code);
  }
  
  private onMouseMove(e: MouseEvent): void {
    if (this.isPointerLocked) {
      this.mouseMovement.x += e.movementX;
      this.mouseMovement.y += e.movementY;
    }
  }
  
  private onMouseDown(e: MouseEvent): void {
    this.mouseButtons.add(e.button);
    
    // Left click for ability
    if (e.button === 0) {
      this.onAbilityUse?.();
    }
  }
  
  private onMouseUp(e: MouseEvent): void {
    this.mouseButtons.delete(e.button);
  }
  
  public requestPointerLock(): void {
    document.body.requestPointerLock();
  }
  
  public isKeyPressed(code: string): boolean {
    return this.keys.has(code);
  }
  
  public isMouseButtonPressed(button: number): boolean {
    return this.mouseButtons.has(button);
  }
  
  public getMouseMovement(): { x: number; y: number } {
    const movement = { ...this.mouseMovement };
    this.mouseMovement = { x: 0, y: 0 };
    return movement;
  }
  
  public get pointerLocked(): boolean {
    return this.isPointerLocked;
  }
  
  // Get movement vector from WASD
  public getMovementVector(): { x: number; z: number } {
    let x = 0;
    let z = 0;
    
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) z -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) z += 1;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1;
    
    // Normalize diagonal movement
    const length = Math.sqrt(x * x + z * z);
    if (length > 0) {
      x /= length;
      z /= length;
    }
    
    return { x, z };
  }
  
  public isSprinting(): boolean {
    return this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
  }
}
